import { CustomCheckbox, InfiniteScroll, ReuseModal } from "components/ui";
import { memo, useEffect, useRef, useState } from "react";
import { SearchIcon } from "../../../images/SearchIcon";
import { CustomInput } from "../../CustomInput";
import {
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { ManageSectionsModals, PlaybookFilter } from "../../../../types/enums";
import {
  campaignsApi,
  useGeneratePlaybooksForAccountsMutation,
  useGetAccountsForPlaybooksQuery,
} from "../../../../api";
import { useParams } from "react-router-dom";
import { useAppDispatch, useDebounceValue } from "../../../../hooks";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { setIsNotificationsOpen } from "../../../../features/companySlice";
import { ManageSectionsBlock } from "../../ManageSectionsBlock";
import { TwoPersonIcon } from "../../../images/TwoPersonIcon";
import { GeneratePlaybookListItem } from "../../../../types";

const ListItem = memo(
  ({
    id,
    fullName,
    checked,
    onClick,
    totalCount,
  }: {
    id: string;
    fullName: string;
    checked: boolean;
    onClick: () => void;
    totalCount?: number;
  }) => {
    return (
      <MenuItem
        sx={{
          padding: "8px 16px 8px 8px",
          justifyContent: "space-between",
          gap: "16px",
        }}
        value={id}
        onClick={onClick}
      >
        <Stack flexDirection={"row"} alignItems={"center"}>
          <CustomCheckbox checked={checked} onChange={() => {}} />
          <Stack>
            <Typography
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#232323",
                textWrap: "wrap",
              }}
            >
              {fullName}
            </Typography>
            {totalCount !== undefined && (
              <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
                <TwoPersonIcon
                  color={"#232323"}
                  secondColor={"#232323"}
                  width={"12px"}
                  height={"12px"}
                />
                <Typography
                  component="span"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#232323",
                    textWrap: "wrap",
                  }}
                >
                  {totalCount} Targeted Contacts
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </MenuItem>
    );
  },
);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  setIsLoading: (isLoading: boolean) => void;
};

const PER_PAGE = 25;

export const GeneratePlaybookForAccountsModal = ({
  isOpen,
  onClose,
  setIsLoading,
}: Props) => {
  const { campaign_id } = useParams();
  const dispatch = useAppDispatch();

  const [searchValue, setSearchValue] = useState<string>("");
  const [accounts, setAccounts] = useState<GeneratePlaybookListItem[]>([]);
  const [selected, setSelected] = useState<{ [key: string]: boolean }>({});
  const [page, setPage] = useState<number>(1);
  const [isHide, setIsHide] = useState<boolean>(false);

  const wrapper = useRef<HTMLDivElement>(null);

  const debounceValue = useDebounceValue(searchValue);

  const [generatePlaybooks, { isLoading: isGeneratingPlaybooks }] =
    useGeneratePlaybooksForAccountsMutation();

  const {
    data,
    isLoading: isLoadingMembers,
    isFetching,
  } = useGetAccountsForPlaybooksQuery({
    page,
    per_page: PER_PAGE,
    search: debounceValue.trim() || undefined,
    campaign_id: campaign_id!,
    filter: PlaybookFilter.without_playbooks,
  });

  const canLoadMore = page < (data?.total_pages || 1);

  useEffect(() => {
    setPage(1);
  }, [debounceValue]);

  useEffect(() => {
    setIsLoading(isLoadingMembers);
  }, [isLoadingMembers]);

  useEffect(() => {
    setSelected((prev) => {
      const updated = { ...prev };
      accounts.forEach(({ id }) => {
        if (!(id in updated)) updated[id] = true;
      });
      return updated;
    });
  }, [accounts]);

  const selectMemberHandler = (id: string) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const isSelectAll =
    accounts.length > 0 &&
    accounts.every((member) => selected[member.id] === true);

  const toggleAllHandler = () => {
    const checked = !isSelectAll;
    setSelected((prev) =>
      Object.fromEntries(Object.keys(prev).map((key) => [key, checked])),
    );
  };

  const selectedIds = Object.entries(selected)
    .filter(([_, value]) => value)
    .map(([key]) => key);

  const generatePlaybooksHandler = () => {
    if (campaign_id) {
      generatePlaybooks({
        campaign_id,
        account_ids: selectedIds,
        include_all: isSelectAll,
      })
        .unwrap()
        .then(() => {
          dispatch(campaignsApi.util.invalidateTags(["activities"]));
          dispatch(setIsNotificationsOpen(true));

          onClose();
        })
        .catch((err) =>
          toast.error(
            <CustomToast
              type="error"
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
              title="Playbook generation failed."
            />,
          ),
        );
    }
  };

  if (!data) return null;

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate BDR Engagement Playbooks"
      maxWidth="540px"
      submitBtnText="Generate Guides"
      submitBtnColor="#1C3C6C"
      onSubmit={generatePlaybooksHandler}
      isLoading={isGeneratingPlaybooks}
      isDisabled={isGeneratingPlaybooks || selectedIds.length === 0}
      isHide={isHide}
      isDisabledCancelBtn={isGeneratingPlaybooks}
    >
      <InfiniteScroll<GeneratePlaybookListItem>
        hasMore={canLoadMore}
        isLoading={isFetching}
        loadMore={() => setPage((prev) => prev + 1)}
        page={page}
        items={data?.items || []}
        scrollOffset={50}
        containerRef={wrapper}
        setAllItems={setAccounts}
      >
        {({ items }) => (
          <Stack gap={"16px"}>
            <Stack gap={"8px"}>
              <Stack gap={"12px"}>
                <Typography
                  component="span"
                  variant="subtitle1"
                  sx={{ color: "#464646", fontWeight: 500 }}
                >
                  Which accounts would you like to generate Playbooks for?
                </Typography>
                <CustomInput
                  value={searchValue}
                  placeholder="Search"
                  prefixIcon={
                    isFetching && page === 1 ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  onChange={setSearchValue}
                  clearButton
                  minHeight="42px"
                />
              </Stack>

              <Stack>
                <Stack
                  ref={wrapper}
                  maxHeight={"320px"}
                  sx={{ overflowY: "auto" }}
                >
                  {items.length === 0 && !isFetching && (
                    <Typography
                      variant="body1"
                      sx={{ color: "#797979", padding: "8px 12px" }}
                    >
                      No contacts with such name found
                    </Typography>
                  )}

                  {items.map(({ name, id, available_for_playbook_count }) => (
                    <ListItem
                      key={id}
                      fullName={name}
                      onClick={() => selectMemberHandler(id)}
                      id={id}
                      checked={selected[id] || false}
                      totalCount={available_for_playbook_count}
                    />
                  ))}
                  {canLoadMore && isFetching && (
                    <Stack
                      alignItems={"center"}
                      justifyContent={"center"}
                      flexGrow={1}
                      minHeight={"30px"}
                    >
                      <CircularProgress size={20} sx={{ color: "#1C3C6C" }} />
                    </Stack>
                  )}
                </Stack>

                {items.length > 0 && (
                  <>
                    <Divider style={{ color: "#D9D9D9", margin: 0 }} />
                    <ListItem
                      fullName={`Select all: ${data?.count || 0}`}
                      onClick={toggleAllHandler}
                      id="Select all"
                      checked={isSelectAll}
                    />
                  </>
                )}
              </Stack>
            </Stack>

            <ManageSectionsBlock
              adjustSection={"generated BDR Engagement Playbook"}
              manageSections={"Playbook"}
              hidePrevModalHandler={setIsHide}
              closePrevModalHandler={onClose}
              modal={ManageSectionsModals.playbook}
            />
          </Stack>
        )}
      </InfiniteScroll>
    </ReuseModal>
  );
};
